/*
 * Company load for a proposal, shared by the details page and the display modal.
 * The user is loaded first. The page logs that payload; the modal does not.
 * Without an RFC the company, sector, and address stay unset.
 * The company row is stored before the sector lookup, including when the sector id is absent.
 * Sector is requested only when company.sectorEconomico is set.
 * Address reference data uses direccion[1], the same index both screens used before.
 * Country, state, town, postal code, and neighborhood load together.
 * Each setter receives the response data property, not the whole response.
 * The page logs those five payloads after the setters run. The modal returns earlier.
 * Failures are not caught here. useProposalCompany logs "Error fetching proposal data:".
 */
import { logger } from "../../utils/logger.js";
import { getCompanyByRFCForUser } from "../../logic/company/index.js";
import { getSectorById } from "../../logic/proposal/index.js";
import { getUser } from "../../logic/user";
import { getReferenceDataById } from "../../logic/companyEdit";

/**
 * Loads the proposing user's company, sector, and address reference rows.
 * The details page passes logSteps so the same records stay in the console.
 * The modal leaves logSteps off and only keeps the error log in its caller.
 * A missing RFC or address stops the later lookups and leaves those fields unset.
 * @param {string} userId Proposing user id
 * @param {Function} navigate
 * @param {Function} setCompany
 * @param {Function} setSector
 * @param {Function} setCountry
 * @param {Function} setState
 * @param {Function} setTown
 * @param {Function} setZipCode
 * @param {Function} setNeighborhood
 * @param {boolean} [logSteps]
 */
export const loadProposalCompany = async ({
  userId,
  navigate,
  setCompany,
  setSector,
  setCountry,
  setState,
  setTown,
  setZipCode,
  setNeighborhood,
  logSteps = false,
}) => {
  const userData = await getUser(userId, navigate);
  if (logSteps) {
    logger.info("userData", { data: userData });
  }
  if (!(userData && userData.RFC)) {
    return;
  }

  const companyData = await getCompanyByRFCForUser(
    userData._id,
    userData.RFC,
    navigate,
  );
  setCompany(companyData);
  if (logSteps) {
    logger.info("companyData", { data: companyData });
  }

  if (companyData?.sectorEconomico) {
    const sectorData = await getSectorById(companyData.sectorEconomico, navigate);
    setSector(sectorData);
    if (logSteps) {
      logger.info("sectorData", { data: sectorData });
    }
  }

  if (!companyData?.direccion?.[1]) {
    return;
  }

  const address = companyData.direccion[1];
  const [countryData, stateData, townData, zipCodeData, neighborhoodData] =
    await Promise.all([
      getReferenceDataById("countries", address.pais, navigate),
      getReferenceDataById("states", address.estado, navigate),
      getReferenceDataById("towns", address.municipio, navigate),
      getReferenceDataById("zipCodes", address.codigoPostal, navigate),
      getReferenceDataById("neighborhoods", address.colonia, navigate),
    ]);

  setCountry(countryData.data);
  setState(stateData.data);
  setTown(townData.data);
  setZipCode(zipCodeData.data);
  setNeighborhood(neighborhoodData.data);

  if (!logSteps) {
    return;
  }
  logger.info("countryData", { data: countryData });
  logger.info("stateData", { data: stateData });
  logger.info("townData", { data: townData });
  logger.info("zipCodeData", { data: zipCodeData });
  logger.info("neighborhoodData", { data: neighborhoodData });
};
